import { TICKET_LIMITS, type TicketDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { Link, Navigate, useLocation, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { MentionPicker } from "../../components/chat/mention-picker"
import { MessageMarkdown } from "../../components/chat/message-markdown"
import { PropertyChat } from "../../components/chat/property-chat"
import { useMentionCompose } from "../../components/chat/use-mention-compose"
import { ApiError } from "../../lib/api-client"
import { getOwnedOrigins } from "../../lib/owned-origins.runtime"
import { formatFullDateTime, getDisplayName } from "../../lib/user-display"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { TicketAttachments } from "../components/tickets/ticket-attachments"
import {
	TicketKindBadge,
	TicketStatusBadge,
} from "../components/tickets/ticket-card"
import { TicketComments } from "../components/tickets/ticket-comments"
import { topicsVisibleForTicketScope } from "../components/tickets/ticket-filter-model"
import {
	buildAssigneeOptions,
	buildTicketMentionCandidates,
	getAllowedTicketStatusTargets,
	isTicketManager,
	selectedMentionCandidatesFromMentions,
	ticketMentionRequestFields,
} from "../components/tickets/ticket-form-model"
import {
	dateTimeLocalValueToIso,
	isoToDateTimeLocalValue,
} from "../components/tickets/ticket-reminder-model"
import { TicketStatusMenu } from "../components/tickets/ticket-status-menu"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProjects } from "../hooks/use-projects"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import {
	useSetTicketStatus,
	useTicket,
	useUpdateTicket,
} from "../hooks/use-tickets"
import { useTopics } from "../hooks/use-topics"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"
import { NotFoundContent } from "./not-found-content"

const CONTROL_FOCUS =
	"focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"

interface TicketDetailLocationState {
	returnTo?: {
		path?: unknown
		label?: unknown
	}
}

export function TicketDetailPage() {
	const { id, ticketId } = useParams<{ id: string; ticketId: string }>()
	const location = useLocation()
	const { data: property, isLoading, error } = useProperty(id)
	const ticketQuery = useTicket(property?.id, ticketId)
	const { setLastPropertyId } = useLastPropertyId()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [shareOpen, setShareOpen] = useState(false)
	const returnTo = safeReturnTo(location.state)

	const propertyNotFound =
		!id || (error instanceof ApiError && error.status === 404)
	const ticketNotFound =
		ticketQuery.error instanceof ApiError && ticketQuery.error.status === 404

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (propertyNotFound) setLastPropertyId(null)
	}, [property, propertyNotFound, setLastPropertyId])

	if (propertyNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{error.message}
			</div>
		)
	}
	if (!property) return null

	const ticket = ticketQuery.data?.ticket

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<div className="mb-4 flex items-center gap-2">
					<Button variant="ghost" size="sm" asChild className="-ml-2">
						<Link to={returnTo?.path ?? webPaths.propertyTickets(property.id)}>
							{returnTo?.label ?? t.tickets.backToList}
						</Link>
					</Button>
				</div>

				{ticketQuery.isLoading ? (
					<div className="text-muted-foreground">{t.common.loading}</div>
				) : ticketNotFound ? (
					<NotFoundContent />
				) : ticketQuery.error ? (
					<div
						role="alert"
						className="rounded-lg border border-destructive bg-destructive px-4 py-3 text-sm text-destructive-foreground"
					>
						{ticketQuery.error.message}
					</div>
				) : ticket ? (
					<TicketDetailContent
						propertyId={property.id}
						propertyName={property.name}
						ticket={ticket}
					/>
				) : null}
			</PageContainer>
			<PropertyBottomNav propertyId={property.id} />
			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			<PropertyChat propertyId={property.id} />
		</div>
	)
}

function TicketDetailContent({
	propertyId,
	propertyName,
	ticket,
}: {
	propertyId: string
	propertyName: string
	ticket: TicketDTO
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const memberships = usePropertyMemberships(propertyId)
	const topics = useTopics(propertyId)
	const projects = useProjects(propertyId)
	const { profile } = useUser()
	const setStatus = useSetTicketStatus(propertyId, ticket.id)
	const updateTicket = useUpdateTicket(propertyId, ticket.id)
	const [statusError, setStatusError] = useState<string | null>(null)
	const [assignError, setAssignError] = useState<string | null>(null)
	const [topicError, setTopicError] = useState<string | null>(null)
	const [reminderError, setReminderError] = useState<string | null>(null)
	const [descriptionError, setDescriptionError] = useState<string | null>(null)
	const [descriptionEditing, setDescriptionEditing] = useState(false)
	const [descriptionDraft, setDescriptionDraft] = useState(
		ticket.description ?? "",
	)
	const [reminderValue, setReminderValue] = useState(() =>
		isoToDateTimeLocalValue(ticket.reminderAt),
	)
	const descriptionRef = useRef<HTMLTextAreaElement | null>(null)
	const descriptionMentionsId = `ticket-description-mentions-${ticket.id}`
	const ownedOrigins = useMemo(getOwnedOrigins, [])
	const assigneeOptions = buildAssigneeOptions(
		memberships.data,
		t.tickets.unassigned,
	)
	const canManageTickets = isTicketManager(memberships.data, profile?.id)
	const viewerId = profile?.id
	const mentionPeople = useMemo(
		() => buildTicketMentionCandidates(memberships.data, viewerId),
		[memberships.data, viewerId],
	)
	const descriptionMention = useMentionCompose({
		people: mentionPeople,
		draft: descriptionDraft,
		setDraft: setDescriptionDraft,
		textareaRef: descriptionRef,
	})
	const canSetStatus =
		canManageTickets || (viewerId != null && ticket.assignee?.id === viewerId)
	const allowedStatuses = getAllowedTicketStatusTargets(
		ticket.status,
		canManageTickets,
	)
	const selectedTopicName =
		topics.data?.find((topic) => topic.id === ticket.topicId)?.name ?? null
	const selectedProject =
		ticket.projectId && projects.data
			? projects.data.find((project) => project.id === ticket.projectId)
			: null
	const visibleTopics = useMemo(
		() => topicsVisibleForTicketScope(topics.data ?? [], ticket.projectId),
		[topics.data, ticket.projectId],
	)

	useEffect(() => {
		setReminderValue(isoToDateTimeLocalValue(ticket.reminderAt))
	}, [ticket.reminderAt])

	useEffect(() => {
		if (!descriptionEditing) setDescriptionDraft(ticket.description ?? "")
	}, [ticket.description, descriptionEditing])

	async function saveDescription(event: FormEvent) {
		event.preventDefault()
		const trimmed = descriptionDraft.trim()
		if (trimmed.length > TICKET_LIMITS.descriptionMax) {
			setDescriptionError(
				t.tickets.formErrors.descriptionTooLong.replace(
					"{max}",
					String(TICKET_LIMITS.descriptionMax),
				),
			)
			return
		}
		const mentions = descriptionMention.resolveMentions(trimmed)
		setDescriptionError(null)
		try {
			await updateTicket.mutateAsync({
				description: trimmed,
				...ticketMentionRequestFields({ ...mentions, includeEmpty: true }),
			})
			descriptionMention.reset()
			setDescriptionEditing(false)
		} catch (err) {
			setDescriptionError(
				err instanceof ApiError ? err.message : t.tickets.formErrors.unknown,
			)
		}
	}

	return (
		<article className="rounded-lg border border-border bg-card p-5 shadow-sm">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<h1 className="text-3xl font-black tracking-tight text-foreground">
						{ticket.title}
					</h1>
					<div className="mt-3 flex flex-wrap items-center gap-2">
						{canSetStatus && allowedStatuses.length > 1 ? (
							<TicketStatusMenu
								status={ticket.status}
								statuses={allowedStatuses}
								pending={setStatus.isPending}
								onSelect={async (status) => {
									setStatusError(null)
									try {
										await setStatus.mutateAsync({ status })
									} catch (err) {
										setStatusError(
											err instanceof ApiError
												? err.message
												: t.tickets.statusChangeError,
										)
									}
								}}
							/>
						) : (
							<TicketStatusBadge status={ticket.status} t={t.tickets} />
						)}
						<TicketKindBadge kind={ticket.kind} t={t.tickets} />
					</div>
				</div>
			</div>

			{statusError ? (
				<div
					role="alert"
					className="mt-4 rounded-lg border border-destructive bg-destructive px-3 py-2 text-sm text-destructive-foreground"
				>
					{statusError}
				</div>
			) : null}

			<div className="mt-6 grid gap-4 sm:grid-cols-2">
				<DetailLinkField
					label={t.tickets.filterProperty}
					value={propertyName}
					to={webPaths.property(propertyId)}
				/>
				{ticket.projectId ? (
					<DetailLinkField
						label={t.tickets.filterProject}
						value={selectedProject?.name ?? t.tickets.filterProject}
						to={webPaths.project(propertyId, ticket.projectId)}
					/>
				) : null}
				{canManageTickets ? (
					<div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
						<label
							htmlFor="ticket-detail-assignee"
							className="text-xs font-medium text-muted-foreground"
						>
							{t.tickets.assigneeLabel}
						</label>
						<select
							id="ticket-detail-assignee"
							value={ticket.assignee?.id ?? ""}
							disabled={updateTicket.isPending}
							onChange={async (e) => {
								setAssignError(null)
								try {
									await updateTicket.mutateAsync({
										assigneeUserId: e.target.value || null,
									})
								} catch (err) {
									setAssignError(
										err instanceof ApiError
											? err.message
											: t.tickets.assignChangeError,
									)
								}
							}}
							className={`mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
						>
							{assigneeOptions.map((option) => (
								<option
									key={option.value || "__unassigned"}
									value={option.value}
								>
									{option.label}
								</option>
							))}
						</select>
						{assignError ? (
							<div role="alert" className="mt-2 text-xs text-destructive">
								{assignError}
							</div>
						) : null}
					</div>
				) : (
					<DetailField
						label={t.tickets.assigneeLabel}
						value={
							ticket.assignee
								? getDisplayName(ticket.assignee)
								: t.tickets.unassigned
						}
					/>
				)}
				{canManageTickets ? (
					<div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
						<label
							htmlFor="ticket-detail-topic"
							className="text-xs font-medium text-muted-foreground"
						>
							{t.tickets.filterTopic}
						</label>
						<select
							id="ticket-detail-topic"
							value={ticket.topicId ?? ""}
							disabled={updateTicket.isPending}
							onChange={async (e) => {
								setTopicError(null)
								try {
									await updateTicket.mutateAsync({
										topicId: e.target.value || null,
									})
								} catch (err) {
									setTopicError(
										err instanceof ApiError
											? err.message
											: t.tickets.topicUpdateError,
									)
								}
							}}
							className={`mt-1 w-full rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
						>
							<option value="">{t.tickets.topicUngrouped}</option>
							{visibleTopics.map((topic) => (
								<option key={topic.id} value={topic.id}>
									{topic.name}
								</option>
							))}
						</select>
						{topicError ? (
							<div role="alert" className="mt-2 text-xs text-destructive">
								{topicError}
							</div>
						) : null}
					</div>
				) : (
					<DetailField
						label={t.tickets.filterTopic}
						value={selectedTopicName ?? t.tickets.topicUngrouped}
					/>
				)}
				{ticket.dueAt ? (
					<DetailField
						label={t.tickets.dueLabel}
						value={formatFullDateTime(ticket.dueAt, locale)}
					/>
				) : null}
				{canManageTickets ? (
					<div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
						<label
							htmlFor="ticket-detail-reminder"
							className="text-xs font-medium text-muted-foreground"
						>
							{t.tickets.reminderLabel}
						</label>
						<div className="mt-1 flex flex-wrap items-center gap-2">
							<input
								id="ticket-detail-reminder"
								type="datetime-local"
								value={reminderValue}
								disabled={updateTicket.isPending}
								onChange={(event) => setReminderValue(event.target.value)}
								className={`min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
							/>
							<Button
								type="button"
								size="sm"
								variant="brand"
								disabled={updateTicket.isPending}
								onClick={async () => {
									setReminderError(null)
									try {
										await updateTicket.mutateAsync({
											reminderAt: dateTimeLocalValueToIso(reminderValue),
										})
									} catch (err) {
										setReminderError(
											err instanceof ApiError
												? err.message
												: t.tickets.reminderChangeError,
										)
									}
								}}
							>
								{t.tickets.reminderSave}
							</Button>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								disabled={updateTicket.isPending || !reminderValue}
								onClick={async () => {
									setReminderError(null)
									try {
										await updateTicket.mutateAsync({ reminderAt: null })
										setReminderValue("")
									} catch (err) {
										setReminderError(
											err instanceof ApiError
												? err.message
												: t.tickets.reminderChangeError,
										)
									}
								}}
							>
								{t.tickets.reminderClear}
							</Button>
						</div>
						{reminderError ? (
							<div role="alert" className="mt-2 text-xs text-destructive">
								{reminderError}
							</div>
						) : null}
					</div>
				) : ticket.reminderAt ? (
					<DetailField
						label={t.tickets.reminderLabel}
						value={formatFullDateTime(ticket.reminderAt, locale)}
					/>
				) : null}
				<DetailField
					label={t.tickets.createdLabel}
					value={formatFullDateTime(ticket.createdAt, locale)}
				/>
				<DetailField
					label={t.tickets.updatedLabel}
					value={formatFullDateTime(ticket.updatedAt, locale)}
				/>
			</div>

			<section className="mt-8">
				<div className="flex items-center justify-between gap-3">
					<h2 className="text-sm font-semibold text-muted-foreground">
						{t.tickets.descriptionTitle}
					</h2>
					{canManageTickets && !descriptionEditing ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => {
								setDescriptionDraft(ticket.description ?? "")
								descriptionMention.setSelectedCandidates(
									selectedMentionCandidatesFromMentions(
										ticket.descriptionMentions,
										mentionPeople,
									),
								)
								setDescriptionEditing(true)
							}}
						>
							{t.tickets.commentEdit}
						</Button>
					) : null}
				</div>
				<div className="mt-2 text-sm leading-relaxed text-foreground">
					{descriptionEditing ? (
						<form className="space-y-2" onSubmit={saveDescription}>
							<div
								role="combobox"
								aria-label={t.tickets.descriptionTitle}
								aria-haspopup="listbox"
								aria-expanded={descriptionMention.open}
								aria-controls={
									descriptionMention.open ? descriptionMentionsId : undefined
								}
								aria-activedescendant={
									descriptionMention.open
										? `${descriptionMentionsId}-option-${descriptionMention.activeIndex}`
										: undefined
								}
								aria-autocomplete="list"
								className="relative"
								tabIndex={0}
							>
								{descriptionMention.open ? (
									<MentionPicker
										id={descriptionMentionsId}
										label={t.tickets.mentionSuggestionsLabel}
										candidates={descriptionMention.candidates}
										activeIndex={descriptionMention.activeIndex}
										onSelect={descriptionMention.select}
										onHover={descriptionMention.setActiveIndex}
									/>
								) : null}
								<textarea
									ref={descriptionRef}
									value={descriptionDraft}
									onChange={descriptionMention.onChange}
									onKeyDown={descriptionMention.onKeyDown}
									onKeyUp={descriptionMention.onCaretMove}
									onClick={descriptionMention.onCaretMove}
									rows={4}
									maxLength={TICKET_LIMITS.descriptionMax + 10}
									className={`w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none disabled:opacity-60 ${CONTROL_FOCUS}`}
									disabled={updateTicket.isPending}
								/>
							</div>
							{descriptionError ? (
								<div role="alert" className="text-xs text-destructive">
									{descriptionError}
								</div>
							) : null}
							<div className="flex justify-end gap-2">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									disabled={updateTicket.isPending}
									onClick={() => {
										setDescriptionDraft(ticket.description ?? "")
										setDescriptionError(null)
										descriptionMention.reset()
										setDescriptionEditing(false)
									}}
								>
									{t.tickets.commentCancel}
								</Button>
								<Button
									type="submit"
									size="sm"
									disabled={updateTicket.isPending}
								>
									{t.tickets.commentSave}
								</Button>
							</div>
						</form>
					) : ticket.description ? (
						<MessageMarkdown ownedOrigins={ownedOrigins}>
							{ticket.description}
						</MessageMarkdown>
					) : (
						<p className="text-muted-foreground">{t.tickets.noDescription}</p>
					)}
				</div>
			</section>

			<section className="mt-8">
				<h2 className="text-sm font-semibold text-muted-foreground">
					{t.tickets.attachmentsTitle}
				</h2>
				<div className="mt-3">
					<TicketAttachments
						propertyId={propertyId}
						ticketId={ticket.id}
						attachments={ticket.attachments ?? []}
						t={t.tickets}
						canDelete={canManageTickets}
						viewerId={viewerId}
						showEmpty
					/>
				</div>
			</section>

			<TicketComments
				propertyId={propertyId}
				ticketId={ticket.id}
				t={t.tickets}
				locale={locale}
				viewerId={viewerId}
				canManageTickets={canManageTickets}
				ownedOrigins={ownedOrigins}
			/>
		</article>
	)
}

function DetailField({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
			<div className="text-xs font-medium text-muted-foreground">{label}</div>
			<div className="mt-1 text-sm text-foreground">{value}</div>
		</div>
	)
}

function DetailLinkField({
	label,
	value,
	to,
}: {
	label: string
	value: string
	to: string
}) {
	return (
		<div className="rounded-lg border border-border bg-muted/30 px-3 py-3">
			<div className="text-xs font-medium text-muted-foreground">{label}</div>
			<Link
				to={to}
				className="mt-1 inline-flex max-w-full rounded-sm text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
			>
				<span className="truncate">{value}</span>
			</Link>
		</div>
	)
}

function safeReturnTo(state: unknown): { path: string; label: string } | null {
	const raw = (state as TicketDetailLocationState | null)?.returnTo
	if (!raw) return null
	if (typeof raw.path !== "string" || typeof raw.label !== "string") return null
	if (!raw.path.startsWith("/") || raw.path.startsWith("//")) return null
	return { path: raw.path, label: raw.label }
}
